import { watchReturnFromPopup } from "../../core/auth/popupReturnWatcher";
import { useCallback, useEffect, useRef, useState } from "react";

import { trackConversionEvent, readShareAttribution } from "../../core/analytics/conversionAnalytics";
import { useAuth } from "../../core/auth/AuthContext";
import { useAuthUI } from "../../core/auth/AuthUIContext";
import { buildAuthBrowserUrl, buildAndroidBrowserIntent } from "../../core/auth/authBrowserHandoff";
import { storePendingAuthNotice } from "../../core/auth/pendingAuthNotice";
import { isInAppBrowser, isAndroidUserAgent } from "../../core/inAppBrowser";
import { usePanelNotice } from "../panelNotice/hooks/usePanelNotice";
import styles from "./QuickAuthPanel.module.css";

// Chemin de connexion le plus court (ecran de connexion et fenetre « Rejoins
// cette sortie ») : Google, ou e-mail + mot de passe et un seul bouton
// « Continuer ». Avec la protection contre l'enumeration des e-mails, Firebase
// renvoie la meme erreur pour « compte inconnu » et « mot de passe faux » :
// on affiche donc « Adresse ou mot de passe incorrect. », avec juste dessous
// « Mot de passe oublie ? » et « Creer un compte » (sans rien affirmer de faux).

const LOGIN_MIN_PASSWORD = 6;
const REGISTER_MIN_PASSWORD = 8;
const UNKNOWN_ACCOUNT_CODES = new Set(["auth/user-not-found", "auth/invalid-credential", "auth/invalid-login-credentials"]);
const LOGIN_FAILED_MESSAGE = "Adresse ou mot de passe incorrect.";
const EXISTING_ACCOUNT_CODES = new Set(["auth/email-already-in-use", "auth/credential-already-in-use"]);

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.providerIcon}>
      <path d="M21.805 12.23c0-.72-.064-1.412-.184-2.077H12v3.932h5.498a4.7 4.7 0 0 1-2.04 3.086v2.563h3.299c1.93-1.778 3.048-4.397 3.048-7.504Z" fill="#4285F4" />
      <path d="M12 22c2.76 0 5.075-.915 6.766-2.481l-3.299-2.563c-.915.613-2.084.976-3.467.976-2.666 0-4.924-1.799-5.73-4.218H2.86v2.644A10 10 0 0 0 12 22Z" fill="#34A853" />
      <path d="M6.27 13.714A5.992 5.992 0 0 1 5.95 12c0-.595.103-1.174.32-1.714V7.642H2.86A10 10 0 0 0 2 12c0 1.611.386 3.133 1.072 4.358l3.198-2.644Z" fill="#FBBC05" />
      <path d="M12 6.068c1.5 0 2.848.516 3.91 1.53l2.93-2.93C17.07 3.02 14.755 2 12 2A10 10 0 0 0 3.072 7.642l3.198 2.644C7.076 7.867 9.334 6.068 12 6.068Z" fill="#EA4335" />
    </svg>
  );
}

function PasswordEyeGlyph({ visible }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2.5 12s3.4-6 9.5-6 9.5 6 9.5 6-3.4 6-9.5 6-9.5-6-9.5-6Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.7" />
      {visible ? <path d="M4 4 20 20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /> : null}
    </svg>
  );
}

function buildVerificationNotice(status, email) {
  if (status === "failed") {
    return {
      type: "error",
      title: "Compte créé",
      message: "L’e-mail de confirmation n’a pas pu être envoyé. Tu pourras réessayer dans Réglages.",
    };
  }
  if (status === "emulated") {
    return {
      type: "info",
      title: "Compte local créé",
      message: "Aucun e-mail réel en mode local. Utilise le lien affiché par l’émulateur.",
    };
  }
  return { type: "success", title: "Compte créé", message: `Un lien de confirmation a été envoyé à ${email}.` };
}

/**
 * @param {object} props
 * @param {() => void} [props.onAuthenticated]  apres une connexion reussie
 *   (la fenetre se ferme ; l'ecran de connexion disparait de lui-meme).
 */
export default function QuickAuthPanel({ onAuthenticated }) {
  const { showNotice } = usePanelNotice();
  const { pendingAuthIntent } = useAuthUI();
  const { loginWithGoogle, login, register, resetPassword, loadingAction, clearAuthError } = useAuth();
  const emailInputRef = useRef(null);
  const inAppBrowser = isInAppBrowser();

  // "choice" : « Creer un compte » (mis en avant) et Google ;
  // "register" : creation directe ; "login" : Continuer (connexion, creation
  // proposee si aucun compte ne correspond).
  const [mode, setMode] = useState("choice");
  const isRegisterMode = mode === "register";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const disabled = loadingAction || Boolean(busy);
  const safeEmail = email.trim();
  const passwordTooShortForAccount = password.length < REGISTER_MIN_PASSWORD;

  // Les erreurs du parcours sont affichees ici, pas dans le bandeau global.
  useEffect(() => {
    clearAuthError();
    return () => clearAuthError();
  }, [clearAuthError]);

  // Formulaire ouvert depuis l'ecran de choix : curseur dans l'e-mail.
  useEffect(() => {
    if (mode !== "choice") emailInputRef.current?.focus({ preventScroll: true });
  }, [mode]);

  const trackSuccess = useCallback((provider) => {
    trackConversionEvent("shared_event_auth_completed", {
      provider,
      share_source: readShareAttribution().shareSource || "direct",
      embedded_browser: inAppBrowser ? 1 : 0,
    });
  }, [inAppBrowser]);

  const handleGoogle = useCallback(async () => {
    setError("");
    setBusy("google");
    // Retour sur l'app sans finir la connexion Google : bouton debloque
    // apres un court delai, sans attendre Firebase (voir popupReturnWatcher).
    const stopWatching = watchReturnFromPopup(() => setBusy((current) => (current === "google" ? null : current)));
    const result = await loginWithGoogle();
    stopWatching();
    setBusy((current) => (current === "google" ? null : current));
    if (result?.cancelled) return;
    if (result?.success && result.data?.mode === "popup") {
      trackSuccess("google");
      clearAuthError();
      onAuthenticated?.();
    } else if (!result?.success && result?.error) {
      setError(result.error);
      clearAuthError();
    }
  }, [clearAuthError, loginWithGoogle, onAuthenticated, trackSuccess]);

  const handleContinue = useCallback(async (event) => {
    event.preventDefault();
    setError("");
    setResetSent(false);
    if (!safeEmail) {
      setError("Saisis ton adresse e-mail.");
      emailInputRef.current?.focus();
      return;
    }
    if (password.length < LOGIN_MIN_PASSWORD) {
      setError(password ? "Ton mot de passe est trop court." : "Saisis ton mot de passe.");
      return;
    }

    setBusy("login");
    const result = await login(safeEmail, password);
    setBusy(null);
    clearAuthError();

    if (result?.success) {
      trackSuccess("email_password");
      onAuthenticated?.();
      return;
    }
    if (result?.code === "auth/wrong-password") {
      setError("Mot de passe incorrect.");
      return;
    }
    if (UNKNOWN_ACCOUNT_CODES.has(result?.code)) {
      setError(LOGIN_FAILED_MESSAGE);
      return;
    }
    setError(result?.error || "La connexion a échoué. Réessaie.");
  }, [clearAuthError, login, onAuthenticated, password, safeEmail, trackSuccess]);

  const handleCreate = useCallback(async () => {
    setError("");
    setResetSent(false);
    if (!safeEmail) {
      setError("Saisis ton adresse e-mail.");
      emailInputRef.current?.focus();
      return;
    }
    if (passwordTooShortForAccount) {
      setError(`Choisis un mot de passe de ${REGISTER_MIN_PASSWORD} caractères minimum.`);
      return;
    }
    if (!hasAcceptedTerms) {
      setError("Accepte les conditions pour créer ton compte.");
      return;
    }

    setBusy("register");
    const result = await register(safeEmail, password);
    setBusy(null);
    clearAuthError();

    if (result?.success) {
      trackSuccess("email_register");
      const notice = { ...buildVerificationNotice(result.data?.verificationEmail?.status, safeEmail), autoDismissMs: 4800 };
      if (!storePendingAuthNotice(notice)) showNotice(notice);
      onAuthenticated?.();
      return;
    }
    if (EXISTING_ACCOUNT_CODES.has(result?.code)) {
      // Elle croyait ne pas avoir de compte : on la ramene a la connexion.
      setMode("login");
      setHasAcceptedTerms(false);
      setError("Tu as déjà un compte avec cette adresse : connecte-toi avec ton mot de passe.");
      return;
    }
    setError(result?.error || "La création du compte a échoué. Réessaie.");
  }, [clearAuthError, hasAcceptedTerms, onAuthenticated, password, passwordTooShortForAccount, register, safeEmail, showNotice, trackSuccess]);

  const switchMode = useCallback((nextMode) => {
    setMode(nextMode);
    setError("");
    setResetSent(false);
    setHasAcceptedTerms(false);
  }, []);

  const handleSubmit = useCallback((event) => {
    if (!isRegisterMode) return handleContinue(event);
    event.preventDefault();
    return handleCreate();
  }, [handleContinue, handleCreate, isRegisterMode]);

  const handleReset = useCallback(async () => {
    setError("");
    setResetSent(false);
    if (!safeEmail) {
      setError("Saisis d’abord ton adresse e-mail.");
      emailInputRef.current?.focus();
      return;
    }
    setBusy("reset");
    const result = await resetPassword(safeEmail);
    setBusy(null);
    clearAuthError();
    if (result?.success) setResetSent(true);
    else setError(result?.error || "L’envoi a échoué. Réessaie.");
  }, [clearAuthError, resetPassword, safeEmail]);

  const termsCheckbox = (
    <label className={styles.terms}>
      <input
        type="checkbox"
        checked={hasAcceptedTerms}
        onChange={(event) => { setHasAcceptedTerms(event.target.checked); setError(""); }}
      />
      <span>
        J’accepte les <a href="/terms.html" target="_blank" rel="noreferrer">conditions d’utilisation</a> et
        j’ai lu la <a href="/privacy.html" target="_blank" rel="noreferrer">politique de confidentialité</a>.
      </span>
    </label>
  );

  const feedback = (
    <>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      {resetSent ? (
        <p className={styles.success} role="status">
          Si un compte existe avec cette adresse, tu vas recevoir un e-mail pour changer ton mot de passe.
        </p>
      ) : null}
    </>
  );

  const browserUrl = inAppBrowser ? buildAuthBrowserUrl(window.location, pendingAuthIntent) : null;
  const googleBlock = (
    <>
    <button type="button" className={styles.googleButton} onClick={handleGoogle} disabled={disabled}>
      <GoogleGlyph />
      <span>{busy === "google" ? "Connexion en cours…" : "Continuer avec Google"}</span>
    </button>
    {inAppBrowser ? (
      <div className={styles.browserNotice}>
        <a
          className={styles.linkButton}
          href={isAndroidUserAgent() ? buildAndroidBrowserIntent(browserUrl) : browserUrl}
          onClick={() => window.history.replaceState(window.history.state, "", browserUrl)}
          target={isAndroidUserAgent() ? undefined : "_blank"}
          rel="noreferrer"
        >
          Ouvrir dans le navigateur pour utiliser Google
        </a>
        {error ? <p>Si Facebook garde cette page ouverte, utilise son menu ⋯ puis « Ouvrir dans le navigateur ». Ta sortie est conservée.</p> : null}
      </div>
    ) : null}
    </>
  );

  if (mode === "choice") {
    return (
      <div className={styles.panel} data-auth-view="choice">
        <button type="button" className={styles.createAccountButton} onClick={() => switchMode("register")} disabled={disabled}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={styles.providerIcon}>
            <circle cx="10" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.9" />
            <path d="M3.5 19.5c.7-3.4 3.3-5.5 6.5-5.5 1.4 0 2.7.4 3.8 1.1" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
            <path d="M18 14v6M15 17h6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          </svg>
          <span>Créer un compte</span>
        </button>
        {googleBlock}
        {feedback}
        <button type="button" className={styles.loginButton} onClick={() => switchMode("login")} disabled={disabled}>
          <span className={styles.loginButtonMuted}>Déjà un compte ?</span>
          <span className={styles.loginButtonStrong}>Se connecter</span>
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className={styles.loginButtonArrow}>
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className={styles.panel} data-auth-view={mode}>
      <div className={styles.formHeader}>
        <button type="button" className={styles.backButton} onClick={() => switchMode("choice")} disabled={disabled} aria-label="Retour">
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M10 3.5 5.5 8l4.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h3 className={styles.formTitle}>{isRegisterMode ? "Crée ton compte" : "Connexion"}</h3>
      </div>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <input
          ref={emailInputRef}
          className={styles.input}
          type="email"
          name="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck="false"
          placeholder="Adresse e-mail"
          aria-label="Adresse e-mail"
          value={email}
          onChange={(event) => { setEmail(event.target.value); setError(""); setResetSent(false); }}
        />
        <span className={styles.passwordField}>
          <input
            className={`${styles.input} ${styles.passwordInput}`}
            type={showPassword ? "text" : "password"}
            name="password"
            autoComplete={isRegisterMode ? "new-password" : "current-password"}
            placeholder={isRegisterMode ? "Choisis un mot de passe" : "Mot de passe"}
            aria-label={isRegisterMode ? "Choisis un mot de passe" : "Mot de passe"}
            value={password}
            onChange={(event) => { setPassword(event.target.value); setError(""); }}
          />
          <button
            type="button"
            className={styles.eyeButton}
            onClick={() => setShowPassword((visible) => !visible)}
            aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            aria-pressed={showPassword}
          >
            <PasswordEyeGlyph visible={showPassword} />
          </button>
        </span>

        {isRegisterMode ? (
          <>
            <p className={styles.passwordHint}>{REGISTER_MIN_PASSWORD} caractères minimum.</p>
            {termsCheckbox}
          </>
        ) : null}

        {feedback}

        <button type="submit" className={styles.primaryButton} disabled={disabled}>
          {isRegisterMode
            ? busy === "register" ? "Création en cours…" : "Créer mon compte"
            : busy === "login" ? "Connexion…" : "Continuer"}
        </button>

        {isRegisterMode ? (
          <button type="button" className={styles.forgotButton} onClick={() => switchMode("login")} disabled={disabled}>
            J’ai déjà un compte : me connecter
          </button>
        ) : (
          <div className={styles.formLinks}>
            <button type="button" className={styles.linkButton} onClick={handleReset} disabled={disabled}>
              {busy === "reset" ? "Envoi…" : "Mot de passe oublié ?"}
            </button>
            <button type="button" className={styles.createLink} onClick={() => switchMode("register")} disabled={disabled}>
              Créer un compte
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
